import React, { useMemo, useRef, useState } from "react";
import TableContainer from "Common/TableContainer";
import { useFormik } from "formik";
import { tableInitialValues, tableValidationSchema } from "./validationSchema";
import { SubSection } from "context/BlogContext";
import { FileEdit, Save, Trash2 } from "lucide-react";
import { createSelector } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import DeleteModal from "Common/DeleteModal";

interface TableProps {
  sectionOrder: number;
  subSectionOrder: number;
  handleSaveSubSection: (
    sectionOrder: number,
    subSectionInfo: SubSection
  ) => void;
  handleEditSubSection: (sectionOrder: number, subSection: SubSection) => void;
  subSection: SubSection;
  handleDeleteSubSection: (subsectionOrder: number) => void;
}

interface Row {
  [key: string]: string; // Dynamic keys for row data
}

export interface FormValues {
  title: string;
  headers: string[];
  rows: string[][]; // Change this to an array of arrays of strings
}

const Table: React.FC<TableProps> = ({
  sectionOrder,
  subSectionOrder,
  handleSaveSubSection,
  handleEditSubSection,
  subSection,
  handleDeleteSubSection,
}) => {
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [isCancel, setIsCancel] = useState(false);

  const selectDataList = createSelector(
    (state: any) => state.Blog,
    (state) => ({
      isLoading: state.isLoading,
    })
  );

  const { isLoading } = useSelector(selectDataList);

  const validation = useFormik<FormValues>({
    initialValues: tableInitialValues(subSection),
    validationSchema: tableValidationSchema,
    onSubmit: (values) => {
      // Remove empty headers
      const filteredHeaders = values.headers.filter(
        (header) => header.trim() !== ""
      );

      // Remove empty rows and remove empty strings inside each row
      const filteredRows = values.rows
        .map((row) => row.filter((value) => value.trim() !== "")) // Remove empty values in each row
        .filter((row) => row.length > 0); // Keep only rows that have at least one non-empty value

      const tableData = {
        headers: filteredHeaders,
        rows: filteredRows,
      };

      handleSaveSubSection(sectionOrder, {
        title: values.title,
        type: "table",
        attrs: { tableData },
        isEditing: false,
        order: subSectionOrder,
      });
    },
  });

  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const handleInputChange = (
    rowIndex: number,
    columnIndex: number,
    value: string
  ) => {
    const newRows = [...validation.values.rows];
    newRows[rowIndex][columnIndex] = value;
    validation.setFieldValue("rows", newRows);
    // Restore focus after state update
    setTimeout(() => {
      inputRefs.current[`${rowIndex}-${columnIndex}`]?.focus();
    }, 0);
  };

  const handleAddRow = (rowIndex: number, columnIndex: number) => {
    const currentRow = validation.values.rows[rowIndex];

    if (validation.values.rows.length - 1 === rowIndex) {
      const isCellsFilled = currentRow.some((cell) => cell.trim() !== "");

      if (isCellsFilled) {
        const newRow = Array(currentRow.length).fill("");
        validation.setFieldValue("rows", [...validation.values.rows, newRow]);
        // Restore focus after state update
        setTimeout(() => {
          inputRefs.current[`${rowIndex}-${columnIndex}`]?.focus();
        }, 0);
      }
    }
  };

  const handleRemoveRow = (rowIndex: number) => {
    const newRows = validation.values.rows.filter(
      (_, index) => index !== rowIndex
    );
    validation.setFieldValue("rows", newRows);
  };

  const handleHeaderChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const newHeaders = [...validation.values.headers];
    newHeaders[index] = e.target.value;

    // If the last header is typed, add a new one
    if (index === newHeaders.length - 1 && e.target.value !== "") {
      newHeaders.push("");
    }

    validation.setFieldValue("headers", newHeaders);
  };

  const columns = useMemo(
    () =>
      validation.values.headers.map((header, columnIndex) => ({
        header: (
          <div className="flex flex-col gap-3">
            <input
              type="text"
              value={header}
              name={`header[${columnIndex}]`}
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder={`Add Header`}
              onChange={(e) => handleHeaderChange(e, columnIndex)}
            />
            {validation.errors.headers && validation.errors.headers && (
              <div className="text-red-500">{validation.errors.headers}</div>
            )}
          </div>
        ),
        id: `column-${columnIndex}`,
        accessorKey: header.toLowerCase(),
        enableColumnFilter: false,
        cell: (cell: any) => {
          const {
            row: { index },
          } = cell;

          return (
            <div>
              <input
                ref={(el) => {
                  inputRefs.current[`${index}-${columnIndex}`] = el;
                }}
                type="text"
                name={`rows[${index}][${header.toLowerCase()}]`}
                value={validation.values.rows[index]?.[columnIndex] || ""}
                onChange={(e) =>
                  handleInputChange(index, columnIndex, e.target.value)
                }
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder={`Add ${header}`}
                onFocus={() => {
                  handleAddRow(index, columnIndex); // Add a new row if the last input is not empty
                }}
              />
              {validation.errors.rows &&
                validation.errors.rows[index] &&
                validation.errors.rows[index][columnIndex] && (
                  <div className="text-red-500">
                    {validation.errors.rows[index][columnIndex]}
                  </div>
                )}
            </div>
          );
        },
      })),
    [validation.values.headers, validation.values.rows]
  );

  const filteredHeaders = useMemo(
    () => validation.values.headers.filter((header) => header.trim() !== ""),
    [validation.values.headers]
  );

  const filteredRows = useMemo(
    () =>
      validation.values.rows.filter((row) =>
        row.some((value) => value.trim() !== "")
      ),
    [validation.values.rows]
  );

  const previewColumns = useMemo(
    () =>
      filteredHeaders.map((header, columnIndex) => ({
        header: (
          <div className="flex flex-col gap-3">
            <span className="font-semibold">{header}</span>
          </div>
        ),
        id: `column-${columnIndex}`,
        accessorKey: `column-${columnIndex}`,
        enableColumnFilter: false,
        cell: (cell: any) => {
          const {
            row: { index },
          } = cell;
          return <div>{filteredRows[index]?.[columnIndex] || "-"}</div>;
        },
      })),
    [filteredHeaders, filteredRows]
  );

  const tableData = useMemo(
    () => validation.values.rows,
    [validation.values.rows]
  );

  // Handle delete confirmation
  const handleDelete = () => {
    handleDeleteSubSection(subSection.order);
    setDeleteModal(false);
  };

  const handleCancelSubSection = () => {
    handleSaveSubSection(sectionOrder, {
      ...subSection,
      isEditing: false,
    });
    validation.resetForm();
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    validation.handleSubmit();
  };

  return (
    <div>
      <DeleteModal
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        onDelete={handleDelete}
        message="Are you sure you want to remove this subsection?"
      />
      {subSection.isEditing ? (
        <form onSubmit={handleSubmit}>
          <div className="xl:col-span-6 pt-5">
            <label
              htmlFor={`subTitle`}
              className="inline-block mb-2 text-base font-medium"
            >
              Title
            </label>
            <input
              type="text"
              id="title"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Title"
              name="title"
              onChange={validation.handleChange}
              value={validation.values.title}
            />
          </div>
          <div className="max-w-full">
            <TableContainer
              isPagination={false}
              columns={columns || []}
              data={tableData}
              customPageSize={10}
              divclassName={`mt-7 overflow-x-auto`}
              tableclassName={`w-full min-w-max whitespace-nowrap`}
              theadclassName="text-center bg-slate-100 dark:bg-zink-600"
              thclassName="px-3.5 py-2.5 text-center font-semibold text-slate-500 border-b border-slate-200 dark:border-zink-500 dark:text-zink-200"
              tdclassName="px-3.5 py-2.5 border-y text-center border-slate-200 dark:border-zink-500"
              PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
            />
          </div>
        </form>
      ) : (
        <div className="">
          <TableContainer
            isPagination={false}
            columns={previewColumns || []}
            data={filteredRows}
            customPageSize={10}
            divclassName="overflow-x-auto border border-gray-300 dark:border-zink-500 rounded-lg"
            tableclassName="w-full whitespace-nowrap"
            theadclassName="text-center bg-slate-100 dark:bg-zink-600"
            thclassName="px-3.5 py-2.5 text-center font-semibold text-slate-500 border-b border-slate-200 dark:border-zink-500 dark:text-zink-200"
            tdclassName="px-3.5 py-2.5 border-y text-center border-slate-200 dark:border-zink-500"
            PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
          />
        </div>
      )}
      <div className="flex justify-end gap-x-2.5 pt-5">
        {subSection.isEditing ? (
          <div className="flex gap-x-3">
            {isCancel && (
              <button
                type="button"
                className={`${
                  isLoading ? "cursor-not-allowed opacity-50" : ""
                } text-red-500`}
                onClick={() => handleCancelSubSection()}
                disabled={isLoading}
              >
                <div className="flex align-center">
                  <span className="ml-1">Cancel</span>
                </div>
              </button>
            )}
            <button
              type="button"
              className={`text-green-500 ${
                isLoading
                  ? "cursor-not-allowed opacity-50"
                  : "hover:bg-green-600"
              } bg-green-100 btn hover:text-white focus:text-white focus:bg-green-600 focus:ring focus:ring-green-100 active:text-white active:bg-green-600 active:ring active:ring-green-100 dark:bg-green-500/20 dark:text-green-400 dark:hover:bg-green-500 dark:hover:text-white dark:focus:bg-green-500 dark:focus:text-white dark:active:bg-green-500 dark:active:text-white dark:ring-green-400/20`}
              onClick={(e) => handleSubmit(e)}
              disabled={isLoading}
            >
              <div className="flex align-center">
                <Save className="size-4"></Save>
                <span className="ml-1">Save</span>
              </div>
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              className={`text-custom-500 ${
                isLoading
                  ? "cursor-not-allowed opacity-50"
                  : "hover:bg-custom-600 hover:text-white"
              } btn bg-custom-100 focus:text-white focus:bg-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:ring active:ring-custom-100 dark:bg-custom-500/20 dark:text-custom-500 dark:hover:bg-custom-500 dark:hover:text-white dark:focus:bg-custom-500 dark:focus:text-white dark:active:bg-custom-500 dark:active:text-white dark:ring-custom-400/20`}
              onClick={() => {
                handleEditSubSection(sectionOrder, subSection);
                setIsCancel(true);
              }}
              disabled={isLoading}
            >
              <div className="flex align-center">
                <FileEdit className="size-4"></FileEdit>
                <span className="ml-1">Edit</span>
              </div>
            </button>
          </>
        )}
        <button
          type="button"
          className={`${
            isLoading ? "cursor-not-allowed opacity-50" : ""
          } text-red-500`}
          onClick={() => setDeleteModal(true)}
          disabled={isLoading}
        >
          <div className="flex align-center">
            <Trash2 className="size-4 text-red-500"></Trash2>
            <span className="ml-1">Delete</span>
          </div>
        </button>
      </div>
    </div>
  );
};

export default Table;
